import * as React from 'react';
import { memo, useMemo } from 'react';

import { Box, HStack, Header, Pressable, Text } from '@rbilabs/universal-components';
import { View } from 'react-native';
import { StyleSheet } from 'react-native';

import Picture from 'components/picture';
import SanityBlockRenderer, { createFixedTypographyConfig } from 'components/sanity-block-renderer';
import { StepProgressBar } from 'components/step-progress-bar';
import { LoyaltyQuestStatus, LoyaltyQuestStepStatus } from 'generated/graphql-gateway';
import { getDesignToken, primitive, spacing } from 'styles/constants';

import { IQuest } from '../loyalty-quest/types';
import { canActivateQuest, isQuestActivated } from '../loyalty-quest/utils';

import { QuestBadge } from './quest-badge';
import { QuestCardPendingOrdersMessage } from './quest-card-pending-orders-message';
import { QuestEndDateMessage } from './quest-end-date-message';
import { QuestCallToAction, QuestCardContainer, StyledLink } from './styled';
import { IQuestCardProps } from './types';

export const QuestCard = memo(
  ({
    image,
    altText,
    title,
    callToAction,
    callToActionText,
    onPressCard,
    onShowTermsPress,
    linkText,
    description,
    badgeText,
    howToRedeem,
    isQuestCompleted,
    steps,
    status,
    endDate,
  }: IQuestCardProps) => {
    // Standardize Sanity content typography with design system tokens
    const contentStylesOverrides = createFixedTypographyConfig({
      fontSize: primitive.fontSizes.sm,
      lineHeight: 'lg',
    });

    const totalSteps = steps?.length || 0;
    const completedSteps =
      steps?.filter(step => step.status === LoyaltyQuestStepStatus.COMPLETED).length || 0;

    const shouldDisplayProgressBarByStatus = [
      LoyaltyQuestStatus.ACTIVATED,
      LoyaltyQuestStatus.NOT_COMPLETED,
      LoyaltyQuestStatus.COMPLETED,
    ].includes(status);

    const CardContent = useMemo(() => {
      return (
        <Box padding="$4">
          <Header variant="headerTwo">{title}</Header>
          {!isQuestCompleted && howToRedeem ? (
            <SanityBlockRenderer
              marginBottom="$4"
              content={howToRedeem}
              {...contentStylesOverrides}
            />
          ) : (
            <Text marginBottom="$4">{description}</Text>
          )}

          {!!steps && shouldDisplayProgressBarByStatus && (
            <View>
              <StepProgressBar totalSteps={totalSteps} completedSteps={completedSteps} />
              <QuestCardPendingOrdersMessage
                totalSteps={totalSteps}
                completedSteps={completedSteps}
              />
            </View>
          )}

          <HStack
            // row reverse to display the link aligned to the right
            justifyContent="space-between"
            alignItems="center"
          >
            {!!callToActionText && (
              <QuestCallToAction
                _text={{ variant: 'copyTwo', fontSize: 'sm' }}
                onPress={callToAction}
              >
                {callToActionText}
              </QuestCallToAction>
            )}
            {!!linkText && (
              <StyledLink
                onPress={e => {
                  e?.stopPropagation();
                  onShowTermsPress?.();
                }}
              >
                {linkText}
              </StyledLink>
            )}
          </HStack>
        </Box>
      );
    }, [
      callToAction,
      callToActionText,
      completedSteps,
      contentStylesOverrides,
      description,
      howToRedeem,
      isQuestCompleted,
      linkText,
      onShowTermsPress,
      shouldDisplayProgressBarByStatus,
      steps,
      title,
      totalSteps,
    ]);

    if (canActivateQuest(status)) {
      return (
        <QuestCardContainer>
          <Pressable onPress={onPressCard}>
            {!!image && (
              <Picture
                width="full"
                borderTopRadius="xl"
                image={image}
                flexDirection="column"
                backgroundColor={getDesignToken('background-menu-customization-image')}
                alt={altText || 'quest image'}
              />
            )}
          </Pressable>
          {CardContent}
        </QuestCardContainer>
      );
    }

    return (
      <QuestCardContainer>
        <View style={styles.questCardHeader}>
          <QuestBadge quest={{ steps, status } as IQuest} />
          {isQuestActivated(status) && <QuestEndDateMessage endDate={endDate} />}
        </View>
        <Pressable onPress={onPressCard}>{CardContent}</Pressable>
      </QuestCardContainer>
    );
  }
);

const styles = StyleSheet.create({
  questCardHeader: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
  },
});
